<section class="bg-star-gray-600 pt-[40px] pb-[60px] lg:pt-[64px] lg:pb-[80px]">
  <div class="mx-auto flex max-w-[1000px] flex-col gap-[40px] px-[20px] lg:flex-row lg:items-center lg:gap-[48px]">
    <div class="flex-1 lg:flex-[1.45]">
      <h1 class="text-[30px] font-medium leading-[1.25] text-white lg:text-[38px] xl:text-[44px]">
        AMLBot Alternative for Crypto Wallet and Transaction Checks
      </h1>
      <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">AMLBot is where a great many people in crypto ran their first AML check, usually in a messenger, usually on a single address. The search for an alternative tends to start later, when checking becomes a business process: several people, an API, a record of who decided what. This page compares the two platforms on what each states publicly about itself.</p>
      <a
        [href]="createAccountHref()"
        target="_blank"
        class="btn btn-white btn-with-arrow mt-[28px] min-h-[52px] w-full max-w-[300px] rounded-[8px] font-harmony font-semibold leading-[18px]"
      >
        <span class="text-[16px] tracking-[0.3px]">Open Business Account</span>
        <mat-icon
          svgIcon="second-arrow-right"
          class="ml-[13px] mr-[5px] max-h-[14px] max-w-[16px] text-black"
        ></mat-icon>
      </a>
      <p class="mt-[16px] text-[14px] leading-[1.6] text-white/50">There is no personal tier: AMLOfficer is entered through a business account, and the terms attached to your volumes appear in that account once you register.</p>
    </div>
    <div class="flex flex-1 justify-center">
      <img
        src="./images/aml-providers.webp"
        alt="AML risk reports from several data providers opened side by side"
        width="337"
        height="213"
        class="w-full max-w-[337px] object-contain"
      />
    </div>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">Why people compare AMLBot with something else</h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      The reasons are practical rather than critical, and they say more about how the buyer has
      changed than about the product.
    </p>
<ul class="mt-[24px] flex flex-col gap-[16px]">
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span><span class="text-white">The check moved from a person to a company.</span> A single trader checking a counterparty and a payment desk screening every incoming transfer are different jobs, and the second one needs accounts, roles and a log.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span><span class="text-white">The API sits behind a threshold.</span> AMLBot states that KYT and KYC API access belongs to Pro+, a plan it describes for businesses running 500+ checks a month and gated behind KYB verification. A team that needs an integration well before that volume has to look at how other vendors gate the same thing.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span><span class="text-white">Three risk levels stop being enough.</span> The Lite plan is described as showing risk as Low, Medium or High, with exact scores from 0% to 100% listed under Pro+. Policies written against a numeric threshold need the number.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span><span class="text-white">You want to know whose data you are reading.</span> AMLBot states that its "risk scoring is based on multiple data sources"; on the public pages we read, those sources are not named.</span>
  </li>
</ul>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">What AMLBot publishes about its platform</h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      Read on 8 September 2026 from amlbot.com and the vendor's own blog. This is a summary of
      published statements, not a review.
    </p>
<ul class="mt-[24px] flex flex-col gap-[16px]">
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span><span class="text-white">Checks in a messenger or on the web.</span> The crypto checker page offers a check through Telegram and WhatsApp as well as the web app, with the call to action "Check Your Wallet".</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span><span class="text-white">Three plans.</span> Lite is described as "designed exclusively for personal use" for fewer than about 50 checks a month, with "1 free AML Check upon registration" and additional checks sold in bundles. Pro is aimed at more than 50 checks a month and adds clustering analysis, PDF reports and sanctions data. Pro+ is described for 500+ checks a month and adds exact risk scores, real-time transaction monitoring, behavioural alerts and KYT and KYC API access, with KYB required.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span><span class="text-white">Monitoring and alerts.</span> The transaction monitoring page states automatic alerts when a transaction reaches a defined risk threshold, periodic automatic re-checks, and a KYT dashboard where alerts are reviewed and classified.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span><span class="text-white">Chains named publicly.</span> "Bitcoin, Ethereum, Tron, BNB Chain, Solana, Polygon, and more", alongside figures the site gives for its own coverage: "60,000+ service providers" checked.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span><span class="text-white">More than screening.</span> Document and biometric KYC, KYB, sanctions and PEP screening, investigations and recovery support, an AI tracing tool and AML training courses are all presented as part of the offering.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span><span class="text-white">Prices are partly published.</span> The plan structure is public and Lite bundles are described as sold at low rates, but the posts state that Pro and Pro+ pricing is "Available on Request". No full price card is published.</span>
  </li>
</ul>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">How AMLOfficer approaches the same work</h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      Both platforms read more than one source, so the honest difference is not "several sources
      versus one" — it is what is published and where the work happens. AMLOfficer states its
      model plainly: four AML data providers, including BitOK, Global Ledger and Beosin, queried
      for the same wallet or transaction. The point of that is disagreement, not volume. Providers
      draw on different datasets, and an address one of them calls clean is regularly flagged by
      another.
    </p>
    <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
      The second difference is the starting point. AMLOfficer is entered through a business
      account rather than a personal check: wallets are checked manually or tracked over time, the
      team is invited into the same account with role management, and alerts can be delivered to a
      Telegram group so a decision does not wait on one inbox. Fast API covers dashboard-side work
      and its reports; Pro API is for pushing AML data into your own systems in real time.
    </p>
    <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
      There is also a service that has no equivalent in a screening plan. Alongside monitoring,
      AMLOfficer's team works on recovering stolen assets — relevant if the wallets you screen
      belong to a business that occasionally has to chase funds rather than only decline them.
    </p>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">Comparison</h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      Dimensions where both platforms publish something. Where a fact is not public, the cell says
      so instead of filling the gap.
    </p>
<div class="mt-[28px] overflow-hidden rounded-[10px] border border-solid border-white/20">
  <div class="hidden bg-[#25282D] lg:grid lg:grid-cols-[1.05fr_1fr_1fr]">
    <div class="px-[20px] py-[16px] text-[15px] font-medium text-white/60">What you are comparing</div>
    <div class="px-[20px] py-[16px] text-[15px] font-medium text-white">AMLOfficer</div>
    <div class="px-[20px] py-[16px] text-[15px] font-medium text-white">AMLBot</div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">Data-provider model</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      Four AML data providers, including BitOK, Global Ledger and Beosin, named publicly.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLBot</span>
      "Risk scoring is based on multiple data sources"; the sources are not named on the public pages.
    </div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] border-t border-solid border-white/10 lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">Where a check starts</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      In a registered business account, in the dashboard or through the API.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLBot</span>
      In Telegram or WhatsApp, or in the web app; one free check is offered at registration.
    </div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] border-t border-solid border-white/10 lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">How risk is expressed</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      Not published in that detail. Check with provider.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLBot</span>
      Low / Medium / High on Lite; exact scores from 0% to 100% listed under Pro+.
    </div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] border-t border-solid border-white/10 lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">API access</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      Fast API and Pro API from the business account.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLBot</span>
      KYT and KYC API access is listed under Pro+, which requires KYB and is described for 500+ checks a month.
    </div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] border-t border-solid border-white/10 lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">Ongoing monitoring</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      Tracked wallets with real-time alerts.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLBot</span>
      Threshold alerts and periodic automatic re-checks; listed under Pro+.
    </div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] border-t border-solid border-white/10 lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">Team workflow</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      Team invitations with role management; alerts to a Telegram group.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLBot</span>
      KYT dashboard where alerts are reviewed and classified.
    </div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] border-t border-solid border-white/10 lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">Identity KYC and KYB</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      Not published as part of the platform.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLBot</span>
      Yes — document and biometric checks, sanctions and PEP screening, KYB.
    </div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] border-t border-solid border-white/10 lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">Published chain coverage</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      Not published on the site; what is covered depends on the four providers behind a check. Check with provider.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLBot</span>
      "Bitcoin, Ethereum, Tron, BNB Chain, Solana, Polygon, and more".
    </div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] border-t border-solid border-white/10 lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">Published plan structure</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      Three published tiers - Pro, Corporate and Enterprise (see <a [routerLink]="'/pricing'" class="text-white underline underline-offset-4">pricing</a>). Per-report rates depend on volumes and are shown in your account after registration.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLBot</span>
      Three tiers with stated volume thresholds; Pro and Pro+ pricing "Available on Request".
    </div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] border-t border-solid border-white/10 lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">Beyond screening</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      Assistance recovering stolen assets.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLBot</span>
      Investigations and recovery support, an AI tracing tool, AML training courses.
    </div>
  </div>
</div>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">When AMLBot is the better fit</h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      Several requirements point back at AMLBot, and pretending otherwise would only cost you time
      later in the evaluation.
    </p>
<ul class="mt-[24px] flex flex-col gap-[16px]">
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>You are one person checking a handful of addresses a month. A personal plan with a free first check and small bundles is built for exactly that, and a business account is overhead you do not need.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>You want the check where you already are. Screening a counterparty inside Telegram, in the middle of a conversation, is a real workflow and AMLBot publishes it as one.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>You need identity verification from the same vendor. Document and biometric KYC, PEP and sanctions screening and KYB are part of the AMLBot offering; AMLOfficer does not publish them.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>You want training or an investigation service packaged with the tooling rather than sourced separately.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>Your policy is already written against the risk model you use today, and re-writing it costs more than the difference between the platforms.</span>
  </li>
</ul>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">What to compare, specifically</h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      A comparison between two aggregating platforms turns on different questions than a comparison
      against a single-vendor analytics suite.
    </p>
<ul class="mt-[24px] flex flex-col gap-[16px]">
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>Which sources are named, and can you tell how many read a given address?</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>What you are shown when the sources do not agree — one merged verdict, or the readings behind it?</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>At what volume the API becomes available, and what verification is required to get it.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>Whether the risk output is a level or a number, and whether your own policy can be expressed in it without translation.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>Where the check happens, and whether that place is auditable: a chat message and a dashboard entry leave very different trails.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>Who else in the team can run a check, and what it takes to add them.</span>
  </li>
</ul>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">Evaluating a switch</h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      Take twenty addresses you have already screened — some you accepted, some you rejected, at
      least one you argued about — and run them through both platforms. You are not testing which
      tool is right about the blockchain; you are testing which output you could have defended, and
      how quickly you reached it.
    </p>
    <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
      Then check the two things that only show up in operation: what the API returns for the same
      address and how much code sits between that response and your decision, and what you can
      export when you leave. Keep the existing screening running throughout. An evaluation is not a
      reason to have a gap in an AML control, and no comparison page is worth one.
    </p>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">Frequently asked questions</h2>
<div class="mt-[24px] flex flex-col gap-[24px]">
  <div>
    <h3 class="text-[19px] font-medium leading-[1.4] text-white lg:text-[21px]">Is AMLOfficer affiliated with AMLBot?</h3>
    <p class="mt-[12px] text-[16px] leading-[1.8] text-white/80">No. AMLOfficer is not affiliated with, endorsed by or a reseller of AMLBot. The comparison on this page is built from each vendor's own public pages.</p>
  </div>
  <div>
    <h3 class="text-[19px] font-medium leading-[1.4] text-white lg:text-[21px]">Do both platforms use more than one data source?</h3>
    <p class="mt-[12px] text-[16px] leading-[1.8] text-white/80">Both say so. AMLOfficer states four AML data providers and names three of them - BitOK, Global Ledger and Beosin. AMLBot states that its risk scoring is based on multiple data sources without naming them publicly.</p>
  </div>
  <div>
    <h3 class="text-[19px] font-medium leading-[1.4] text-white lg:text-[21px]">Can I get API access without a large monthly volume?</h3>
    <p class="mt-[12px] text-[16px] leading-[1.8] text-white/80">AMLBot lists KYT and KYC API access under Pro+, described for 500+ checks a month and requiring KYB. AMLOfficer offers Fast API and Pro API from the business account; the terms attached to your volumes are shown in the account after registration.</p>
  </div>
  <div>
    <h3 class="text-[19px] font-medium leading-[1.4] text-white lg:text-[21px]">Does AMLOfficer offer identity KYC?</h3>
    <p class="mt-[12px] text-[16px] leading-[1.8] text-white/80">Not as a published part of the platform. AMLOfficer's public pages describe wallet and transaction AML checks, monitoring and recovery work. If document-level KYC has to come from the same vendor, that is a point in AMLBot's favour.</p>
  </div>
  <div>
    <h3 class="text-[19px] font-medium leading-[1.4] text-white lg:text-[21px]">Is there a free check?</h3>
    <p class="mt-[12px] text-[16px] leading-[1.8] text-white/80">AMLBot publishes one free check at registration on its Lite plan. AMLOfficer does not publish a free tier; checks run inside a registered account and terms follow your volumes.</p>
  </div>
</div>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <div class="rounded-[10px] border border-solid border-white/20 bg-[#25282D] px-[20px] py-[28px] lg:px-[36px] lg:py-[36px]">
      <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">Run the same wallets through four providers</h2>
      <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">If the reason you are comparing is that one verdict is no longer enough, the test is direct: put addresses you have already judged through a platform that reads several providers and see where they diverge. A business account opens the dashboard, tracked wallets and both API surfaces.</p>
      <div class="mt-[28px] flex flex-col gap-[16px] sm:flex-row sm:items-center">
        <a
          [href]="createAccountHref()"
          target="_blank"
          class="btn btn-white btn-with-arrow min-h-[52px] w-full max-w-[300px] rounded-[8px] font-harmony font-semibold leading-[18px]"
        >
          <span class="text-[16px] tracking-[0.3px]">Open Business Account</span>
          <mat-icon
            svgIcon="second-arrow-right"
            class="ml-[13px] mr-[5px] max-h-[14px] max-w-[16px] text-black"
          ></mat-icon>
        </a>
        <a
          [routerLink]="'/contact-us'"
          class="text-[16px] leading-[1.6] text-white underline underline-offset-4 hover:text-white/70"
          >Contact Us</a
        >
      </div>
      <p class="mt-[24px] text-[15px] leading-[1.7] text-white/60">
        Weighing an enterprise analytics suite in the same evaluation? That side of the market is on the <a [routerLink]="'/alternatives/chainalysis'" class="text-white underline underline-offset-4 hover:text-white/70">Chainalysis alternative page</a>.
      </p>
    </div>
  </div>
</section>

<section class="bg-star-gray-600 pb-[70px] lg:pb-[90px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[20px] font-medium leading-[1.4] text-white lg:text-[22px]">Sources and date checked</h2>
    <p class="mt-[16px] text-[15px] leading-[1.7] text-white/60">
      Everything attributed to AMLBot above comes from amlbot.com and the company's own blog, read
      on 8 September 2026. Plans and thresholds change more often than product pages do, so confirm
      the current tier before you decide on it.
    </p>
    <ul class="mt-[16px] flex flex-col gap-[10px] text-[15px] leading-[1.7] text-white/60">
        <li>
          <a href="https://amlbot.com/" target="_blank" rel="nofollow noopener" class="underline underline-offset-4 hover:text-white">AMLBot - homepage</a>
        </li>
        <li>
          <a href="https://amlbot.com/crypto-checker" target="_blank" rel="nofollow noopener" class="underline underline-offset-4 hover:text-white">AMLBot - crypto checker page</a>
        </li>
        <li>
          <a href="https://amlbot.com/transaction-monitoring" target="_blank" rel="nofollow noopener" class="underline underline-offset-4 hover:text-white">AMLBot - transaction monitoring page</a>
        </li>
        <li>
          <a href="https://amlbot.com/api-integration" target="_blank" rel="nofollow noopener" class="underline underline-offset-4 hover:text-white">AMLBot - API integration page</a>
        </li>
        <li>
          <a href="https://blog.amlbot.com/amlbot-plans-explained/" target="_blank" rel="nofollow noopener" class="underline underline-offset-4 hover:text-white">AMLBot - plans explained (vendor blog)</a>
        </li>
    </ul>
    <p class="mt-[24px] text-[14px] leading-[1.7] text-white/40">AMLBot is a trademark of its respective owner. AMLOfficer is not affiliated with or endorsed by AMLBot, and the name is used here only to identify the platform being compared.</p>
  </div>
</section>

<app-related-pages [pages]="relatedPages"></app-related-pages>
